<template>
    <div class="visitor-page">
        <div class="visitor-page-head" v-if="!pickerMode">
            <div class="visitor-page-head-item" v-for="menu in getEnabledMenuItems()" :key="menu.key">
                <SupsisButton
                    @click="changePage(menu.key)"
                    :ghost="currentPage !== menu.key"
                    :primary="currentPage === menu.key"
                >
                    {{ menu.title }}
                </SupsisButton>
            </div>
            <div class="visitor-page-head-item">
                <a-popover
                    placement="bottomLeft"
                    trigger="click"
                    overlayClassName="channel-contacts-popover"
                    :visible="channelContactsPopoverVisible"
                    @visibleChange="channelContactsPopoverVisible = $event"
                >
                    <template slot="content">
                        <div class="channel-contacts-menu">
                            <div
                                class="channel-contacts-item"
                                v-for="channel in channelContactsList"
                                :key="channel.key"
                                @click="handleChannelContactClick(channel)"
                            >
                                <PlatformIconViewer :platform="channel.platform" />
                                <span>{{ $t('visitor.' + channel.labelKey) }}</span>
                            </div>
                        </div>
                    </template>
                    <SupsisButton ghost>{{ $t('visitor.channelContacts') }}</SupsisButton>
                </a-popover>
            </div>
        </div>
        <div class="visitor-page-content">
            <div id="visitors" v-if="currentPage === 'visitors'">
                <div class="filter-sidebar" :class="[dropdown && 'closed']">
                    <div class="filter-sidebar-content">
                        <div class="filter-sidebar-search-header">
                            <div class="search-header-title">
                                {{ selectedFavFilter ? selectedFavFilter.name : $t("message.contactSearch") }}
                            </div>
                            <a-tooltip
                                :title="$t(selectedFavFilter ? 'visitor.editOrDeleteFavorite' : 'visitor.saveSearchFavorite')"
                                placement="top"
                            >
                                <SaveFilterModal
                                    v-if="selectedFavFilter"
                                    :value="selectedFavFilter.name"
                                    @save="saveFilter($event, selectedFavFilter)"
                                    @delete="deleteFavFilter(selectedFavFilter.key, selectedFavFilter._id)"
                                >
                                    <template>
                                        <a-icon type="star" theme="filled" />
                                    </template>
                                    <template #content>
                                        <div></div>
                                    </template>
                                </SaveFilterModal>
                                <SaveFilterModal @save="saveFilter" v-else>
                                    <template>
                                        <a-icon type="star" />
                                    </template>
                                    <template #content>
                                        <div></div>
                                    </template>
                                </SaveFilterModal>
                            </a-tooltip>
                        </div>
                        <div class="filter-sidebar-group">
                            <div class="filter-sidebar-group-title">{{ $t("message.favorites") }}</div>
                            <div class="filter-sidebar-group-content">
                                <div class="favorite-items">
                                    <div
                                        class="favorite-item"
                                        v-for="fav of favFilters['page.contact-list'] || []"
                                        :key="fav._id"
                                        :class="{ selected: selectedFavFilter && selectedFavFilter._id === fav._id }"
                                    >
                                        <div class="favorite-item-content" @click="selectFavFilter(fav)">
                                            <div class="favorite-item-icon material-icons">star</div>
                                            <div class="favorite-item-text">{{ fav.name }}</div>
                                        </div>
                                        <div class="favorite-item-edit-wrapper" @click.stop>
                                            <SaveFilterModal
                                                :value="fav.name"
                                                @save="saveFilter($event, fav)"
                                                @delete="deleteFavFilter(fav.key, fav._id)"
                                            >
                                                <template>
                                                    <div class="favorite-item-edit material-icons">edit</div>
                                                </template>
                                                <template #content>
                                                    <div></div>
                                                </template>
                                            </SaveFilterModal>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="filter-sidebar-group group-filters">
                            <div class="filter-sidebar-group-title">{{ $t("message.filters") }}</div>
                            <div class="filter-sidebar-group-content">
                                <div class="item" v-for="(filter, index) of filteredItems">
                                    <MiniFilterPreview
                                        :filters="filters"
                                        :filter="filter"
                                        @deleteFilter="deleteFilter(index)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="filter-sidebar-footer">
                        <div class="filter-sidebar-control">
                            <Filters align="top" v-model="filteredItems" :items="filters">
                                <template #content>
                                    <div class="control-button">
                                        <div class="material-icons">filter_alt</div>
                                        <div>{{ $t("chatbotv2.addFilter") }}</div>
                                    </div>
                                </template>
                            </Filters>

                            <div class="control-button" v-if="filteredItems.length > 0" @click="clearFilters">
                                <div class="material-icons">filter_alt_off</div>
                                <div>{{ $t("chatbotv2.clearFilter") }}</div>
                            </div>
                        </div>
                        <div class="filter-sidebar-collapse" @click="dropdown = !dropdown">
                            <div class="information-text">{{ $t("message.showHide") }}</div>
                            <div class="material-icons" style="cursor: pointer">
                                {{ dropdown ? "keyboard_double_arrow_right" : "keyboard_double_arrow_left" }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="visitors-content">
                    <div>
                        <div class="tool-head">
                            <div class="left-side">
                                <div class="search-section-wrapper">
                                    <div class="search-section">
                                        <div class="search-container">
                                            <div class="search-field-selector">
                                                <MinimalPopoverSelect
                                                    v-model="searchField"
                                                    :options="searchFieldOptionsWithIcons"
                                                    class="field-selector"
                                                    :show-dropdown-arrow="true"
                                                    :icon="'search'"
                                                    @change="onSearchFieldChange"
                                                />
                                            </div>

                                            <div class="search-input-wrapper">
                                                <input
                                                    class="search-input"
                                                    type="text"
                                                    :placeholder="placeholderText"
                                                    v-model="searchText"
                                                    @keyup.enter="handleSearchInput"
                                                    @input="handleSearchInput"
                                                />
                                            </div>

                                            <div class="search-icon-wrapper">
                                                <i class="material-icons search-icon" v-if="!searchText">search</i>
                                                <button v-else class="clear-search-btn" @click="clearSearch" :title="$t('message.clear')">
                                                    <i class="material-icons">close</i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="searchModePopoverVisible"
                                        class="search-mode-suggestion-popover"
                                        @click.stop
                                        ref="searchSuggestionPopover"
                                    >
                                        <div class="suggestion-header">
                                            <i class="material-icons suggestion-icon">{{ getSearchModeIcon() }}</i>
                                            <span class="suggestion-title">{{ getSearchModeTitle() }}</span>
                                        </div>
                                        <div class="suggestion-message">
                                            {{ getSearchModeMessage() }}
                                        </div>
                                        <div class="suggestion-actions">
                                            <template v-if="detectedSearchMode === 'not_phone' || detectedSearchMode === 'not_email'">
                                                <SupsisButton @click.stop="cancelSearchMode" ghost size="small">
                                                    {{ $t('message.cancel') }}
                                                </SupsisButton>
                                                <SupsisButton @click.stop="switchToAllField" primary size="small">
                                                    {{ $t('visitor.searchInAllFields') }}
                                                </SupsisButton>
                                                <SupsisButton @click.stop="switchToFullnameField" primary size="small">
                                                    {{ $t('visitor.searchInName') }}
                                                </SupsisButton>
                                            </template>
                                            <template v-else>
                                                <SupsisButton @click.stop="cancelSearchMode" ghost size="small">
                                                    {{ $t('message.cancel') }}
                                                </SupsisButton>
                                                <SupsisButton @click.stop="confirmSearchMode" primary size="small">
                                                    {{ $t('message.confirm') }}
                                                </SupsisButton>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="right-side">
                                <a-popover placement="bottomRight" trigger="click">
                                    <template slot="content">
                                        <div v-for="col of allColumns">
                                            <a-checkbox v-model="col.enabled" @change="onChangeCol(col, $event)">
                                                {{ col.title }}
                                            </a-checkbox>
                                        </div>
                                    </template>
                                    <a-button class="flex-center-center">
                                        <div class="material-icons">app_registration</div>
                                    </a-button>
                                </a-popover>
                                <a-button class="flex-center-center" @click="paddingReset">
                                    <div class="material-icons">
                                        {{ paddingReseted ? "fullscreen" : "fullscreen_exit" }}
                                    </div>
                                </a-button>
                                <a-popover placement="bottomRight" trigger="click" overlayClassName="download-popover">
                                    <template slot="content">
                                        <div class="download-options">
                                            <div class="export-csv-visitor-page" @click="exportToCSV">
                                                <div class="material-icons">description</div>
                                                {{ $t("message.downloadThePage") }} (CSV)
                                            </div>
                                            <div
                                                class="export-csv-visitor-page"
                                                :class="{ disabled: visitorDownloding }"
                                                @click="downloadAll"
                                            >
                                                <div class="material-icons">download</div>
                                                {{ $t("message.downloadAllData") }} (XLSX)
                                            </div>

                                            <div
                                                class="export-csv-visitor-page"
                                                :class="{ disabled: visitorDownloding }"
                                                @click="showSaveAsCustomerGroupModal('whatsapp')"
                                            >
                                                <div class="material-icons">save</div>
                                                {{ $t("visitor.saveAsCustomerGroup") }} (Whatsapp)
                                            </div>

                                            <div
                                                class="export-csv-visitor-page"
                                                :class="{ disabled: visitorDownloding }"
                                                @click="showSaveAsCustomerGroupModal('email')"
                                            >
                                                <div class="material-icons">save</div>
                                                {{ $t("visitor.saveAsCustomerGroup") }} (Email)
                                            </div>

                                            <div
                                                class="export-csv-visitor-page"
                                                @click="showImportContactsModal('multiple')"
                                            >
                                                <div class="material-icons">file_upload</div>
                                                {{ $t('visitor.importContacts') }}
                                            </div>

                                            <div
                                                class="export-csv-visitor-page"
                                                @click="showImportContactsModal('single')"
                                            >
                                                <div class="material-icons">person_add</div>
                                                {{ $t('visitor.addNewContact') }}
                                            </div>
                                        </div>
                                    </template>
                                    <a-button class="flex-center-center" style="margin-right: 3px">
                                        <div class="material-icons">more_horiz</div>
                                    </a-button>
                                </a-popover>
                            </div>
                        </div>
                        <supsis-table
                            id="supsis-contacts-list"
                            :class="{ 'visitor-table': true, expended: paddingReseted }"
                            :columns="tableColumns"
                            :rows="visitors"
                            :options="tableOptions"
                            :bottom-pagination="true"
                            :token-based-pagination="true"
                            :pagination-cursor="paginationCursor"
                            @cursor-changed="paginationCursorChanged"
                        >
                            <template #emptyData>
                                <EmptyFilterResult :clear-filters="clearFilters" />
                            </template>
                        </supsis-table>
                    </div>
                </div>
                <Modal2
                    v-if="saveAsCustomerGroupModalVisible"
                    :title="
                        saveAsCustomerGroupModalVisible === 'whatsapp'
                            ? $t('message.saveAsWhatsappCustomerGroup')
                            : $t('message.saveAsEmailCustomerGroup')
                    "
                    @cancel="closeCustomerGroupModal"
                    @ok="saveCustomerGroup"
                >
                    <div class="custom-alert" v-if="saveAsCustomerGroupModalVisible === 'whatsapp'">
                        <div class="custom-alert-icon">
                            <span class="material-icons">info</span>
                        </div>
                        <div class="custom-alert-content">
                            <ul>
                                <li>{{ $t("visitor.filterResultMax5000") }}</li>
                                <li>{{ $t("visitor.onlyContactsPhoneNumber") }}</li>
                            </ul>
                        </div>
                    </div>
                    <a-input v-model="customerGroupName" :placeholder="$t('visitor.enterGroupName')"></a-input>
                </Modal2>
            </div>
            <BannedVisitors v-else-if="currentPage === 'banned-visitors'" />
            <Tags v-else-if="currentPage === 'tags'" />
            <ProfileFields v-else-if="currentPage === 'profile-fields'" />
        </div>
        <Modal2
            v-if="importContactsModalVisible"
            :title="importContactsType === 'multiple' ? $t('visitor.importContactsTitle') : $t('visitor.addSingleContactTitle')"
            @cancel="closeImportContactsModal"
            :showFooter="false"
            :showTitle="false"
            :maskClosable="false"
            :fullwidth="importContactsType === 'multiple'"
            :large="importContactsType === 'single'"
        >
            <ImportContacts
                v-if="importContactsType === 'multiple'"
                :onCancelled="closeImportContactsModal"
                @importContacts="handleImportContacts"
            />
            <SingleContactImport
                v-else
                :onCompleted="onSingleImportCompleted"
                :onCancelled="closeImportContactsModal"
                @addContact="handleAddContact"
            />
        </Modal2>
        <ImportStatusViewer v-if="importStatus" :state="importStatus" @close="importStatus = null" />

        <!-- Channel Contacts Modal -->
        <Modal2
            v-if="selectedChannelContactType"
            :title="getChannelContactModalTitle()"
            @cancel="closeChannelContactsModal"
            :showFooter="false"
            :maskClosable="true"
            xlarge
            className="channel-contacts-modal"
        >
            <WhatsappContactListSwitcher v-if="selectedChannelContactType === 'whatsapp'" />
            <InstagramContactListSwitcher v-else-if="selectedChannelContactType === 'instagram'" />
            <MessengerContactListSwitcher v-else-if="selectedChannelContactType === 'messenger'" />
            <LinkedinContactListSwitcher v-else-if="selectedChannelContactType === 'linkedin'" />
            <MetaLeadsContactListSwitcher v-else-if="selectedChannelContactType === 'metaLeads'" />
        </Modal2>

        <!-- Export Settings Modal -->
        <Modal2
            v-if="showExportSettings"
            :title="$t('visitor.downloadRecordsSettings')"
            @cancel="closeExportSettings"
            className="export-settings-modal keydown-prevent-modal"
            :showFooter="false"
            xlarge
        >
            <ExportSettings
                :fields="visitorFields"
                :records="previewRecords"
                @start="onExportSettingsStart"
                @cancel="closeExportSettings"
            />
        </Modal2>

        <!-- Export Progress Overlay -->
        <div v-if="exportInProgress" class="export-progress-overlay">
            <div class="export-progress-card">
                <div class="title">{{ $t('visitor.excelExport') }}</div>
                <div class="subtitle">{{ $t('visitor.exportTotalRecords', { total: exportProgress.total }) }}</div>
                <a-progress :percent="exportProgress.percent" status="active" />
                <div class="detail">
                    <span>{{ exportProgress.fetched }} / {{ exportProgress.total }}</span>
                    <a-button size="small" style="margin-left: 8px" @click="cancelExport" v-if="exportInProgress">
                        {{ $t('visitor.exportCancel') }}
                    </a-button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb";
import { log2 } from "../../../utils/log2";
import Filters from "./components/Filters.vue";
import MiniFilterPreview from "./components/MiniFilterPreview.vue";
import NoData from "./components/svg/NoData.vue";
import SupsisTable from "../../../components/SupsisTable";

import WhatsappIcon from "../Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import SaveFilterModal from "./components/SaveFilterModal.vue";
import GroupCollapse from "../../../components/GroupCollapse.vue";
import StringUtils from "../../../utils/StringUtils";
import IconButton from "../Settings/components/IconButton.vue";
import _ from "lodash";
import { exportToCSV, isMaskContactInfo, maskContactInfo } from "../../../utils/utils";
import { deleteFilterByKey, getOrUpdateFilters } from "../../../utils/persistenceStorage";
import PlatformIconViewer from "../../../components/common/PlatformIconViewer.vue";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import EmptyFilterResult from "../Chatbot/pages/archive/EmptyFilterResult.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import WHATSAPP_API from "../../../api/whatsapp";
import { MAIL_API } from "../../../api/mail";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import BannedVisitors from "../Settings/pages/chat/BannedVisitors.vue";
import Tags from "../Settings/pages/company/Tags.vue";
import ProfileFields from "../Settings/pages/company/ProfileFields.vue";
import ImportContacts from "./components/ImportContacts.vue";
import SingleContactImport from "./components/SingleContactImport.vue";
import ImportStatusViewer from "./components/ImportStatusViewer.vue";
import { createContact, createMultipleContacts } from "../../../api/visitor";
import { socketMessageStream } from "../../../utils/socketStream";
import { filter } from "rxjs/operators";
import ExportSettings from "../Tables/detail/components/ExportSettings.vue";
import moment from "moment";
import { expertToExcel } from "../../../utils/utils";
import { VISITOR_API } from "../../../api/visitor";
import WhatsappContactListSwitcher from "../IntegrationChannelsItem/WhatsappContactListSwitcher.vue";
import InstagramContactListSwitcher from "../Integrations/instagram/InstagramContactListSwitcher.vue";
import MessengerContactListSwitcher from "../Integrations/messenger/MessengerContactListSwitcher.vue";
import LinkedinContactListSwitcher from "../Integrations/linkedin/LinkedinContactListSwitcher.vue";
import MetaLeadsContactListSwitcher from "../Integrations/meta-leads/MetaLeadsContactListSwitcher.vue";
import MinimalPopoverSelect from "../../../components/popup-chat/chat-window/MinimalPopoverSelect.vue";

const log = log2("Visitors");

const visitorStorageVersion = "v2.0";
const visitorStorageId = `visitor-filters-${visitorStorageVersion}`;
const visitorStorage = getOrUpdateFilters(visitorStorageId);
deleteFilterByKey(visitorStorageId);

export default {
    name: "Visitors",
    components: {
        ProfileFields,
        Tags,
        BannedVisitors,
        SupsisButton,
        IconButton,
        GroupCollapse,
        SaveFilterModal,
        WhatsappIcon,
        AppBreadCrumb,
        Filters,
        MiniFilterPreview,
        NoData,
        SupsisTable,
        PlatformIconViewer,
        EmptyFilterResult,
        Modal2,
        ImportContacts,
        SingleContactImport,
        ImportStatusViewer,
        ExportSettings,
        WhatsappContactListSwitcher,
        InstagramContactListSwitcher,
        MessengerContactListSwitcher,
        LinkedinContactListSwitcher,
        MetaLeadsContactListSwitcher,
        MinimalPopoverSelect,
    },
    props: {
        pickerMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            menus: [
                {
                    title: this.$t("visitor.visitors"),
                    key: "visitors",
                    permissions: ["visitor.list.view"],
                },
                {
                    title: this.$t("visitor.bannedVisitors"),
                    key: "banned-visitors",
                    permissions: ["visitor.list.view", "bannedVisitor.view"],
                },
                {
                    title: this.$t("visitor.tags"),
                    key: "tags",
                    //permissions: ["tags.update", "tags.delete", "tags.create"], //todo implement
                    permissions: ["visitor.list.view"],
                },
                {
                    title: this.$t("visitor.profileFields"),
                    key: "profile-fields",
                    permissions: ["visitor.list.view"],
                },
            ],
            currentPage: this.$route.query.tab || "visitors",
            selectedChannelContactType: null,
            channelContactsPopoverVisible: false,
            dropdown: false,
            filters: [
                {
                    type: "input",
                    name: this.$t("message.name"),
                    material_icons: "person",
                    multiple: false,
                    key: "fullname",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "input",
                    name: this.$t("message.email"),
                    material_icons: "alternate_email",
                    multiple: false,
                    key: "email",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "platform-selector",
                    name: this.$t("chatbotv2.attr.platform"),
                    material_icons: "ads_click",
                    multiple: true,
                    key: "platform",
                    operators: true,
                    defaultValue: ["whatsapp", "instagram", "messenger", "linkedin", "telegram"],
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "date-select",
                    name: this.$t("chatbotv2.attr.last_seen"),
                    material_icons: "date_range",
                    multiple: false,
                    singleton: true,
                    key: "lastSeen",
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "date-select",
                    name: this.$t("message.firstSeen"),
                    material_icons: "date_range",
                    multiple: false,
                    singleton: true,
                    key: "firstSeen",
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "country-selector",
                    name: this.$t("message.country"),
                    material_icons: "location_on",
                    multiple: true,
                    key: "country",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "city-selector",
                    name: this.$t("message.city"),
                    material_icons: "location_on",
                    multiple: false,
                    key: "city",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "os-selector",
                    name: this.$t("message.os"),
                    material_icons: "settings_suggest",
                    multiple: false,
                    key: "os",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "date-select",
                    name: this.$t("message.connectionDate"),
                    material_icons: "date_range",
                    multiple: false,
                    singleton: true,
                    key: "connectionTime",
                },
                {
                    type: "input",
                    name: this.$t("message.visitingPage"),
                    material_icons: "public",
                    multiple: false,
                    key: "visitingPage",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "browser-selector",
                    name: this.$t("message.browser"),
                    material_icons: "computer",
                    multiple: false,
                    key: "browser",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "input",
                    name: this.$t("message.phone"),
                    material_icons: "phone",
                    multiple: false,
                    key: "phone",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "input",
                    name: "WhatsApp Numarası",
                    material_icons: "chat",
                    multiple: false,
                    key: "whatsappNumber",
                    operators: true,
                    omitOperators: ["less_than", "less_or_equal", "greater_than", "greater_or_equal"],
                },
                {
                    type: "select",
                    name: "VIP Müşteri",
                    material_icons: "star",
                    multiple: false,
                    key: "vipCustomer",
                    operators: false,
                    options: [
                        { key: "true", value: "Evet" },
                        { key: "false", value: "Hayır" },
                    ],
                },
                {
                    type: "select",
                    name: "Reklam Var mı?",
                    material_icons: "ads_click",
                    multiple: false,
                    key: "metaReferrals",
                    operators: false,
                    options: [
                        { key: "true", value: "Var" },
                        { key: "false", value: "Yok" },
                    ],
                },
            ],
            filteredItems: this.pickerMode
                ? []
                : (visitorStorage.selectedFavFilter?.length > 0 && visitorStorage.selectedFavFilter) ||
                  (visitorStorage.filters?.length > 0 && visitorStorage.filters) || [
                      {
                          key: "platform",
                          rule: "contains",
                          value: ["whatsapp", "instagram", "messenger", "linkedin", "telegram"],
                      },
                      {
                          key: "connectionTime",
                          rule: "contains",
                          value: "last7",
                      },
                  ],
            filterAccordion: true,
            searchText: visitorStorage.query,
            searchField: visitorStorage.searchField || "all",
            searchFieldOptions: [
                { label: "Tüm Alanlar", value: "all" },
                { label: "İsim", value: "fullname" },
                { label: "Email", value: "email" },
                { label: "Telefon Numarası", value: "phone" },
                { label: "WhatsApp Numarası", value: "whatsappNumber" },
            ],
            showSearchModePopover: false,
            detectedSearchMode: null,
            pendingSearchQuery: null,
            searchModePopoverVisible: false,
            isSelectingFavFilter: false,
            visitors: [],
            staticColumns: [
                {
                    title: this.$t("message.name"),
                    field: "fullname",
                    key: "fullname",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.fullname }}>{row.fullname}</div>;
                    },
                },
                {
                    title: this.$t("message.email"),
                    field: "email",
                    key: "email",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.email }}>{row.email}</div>;
                    },
                },
                {
                    title: this.$t("message.phone"),
                    field: "phone",
                    key: "phone",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div v-tippy={{ content: row.phone || row.whatsappNumber }}>
                                {row.phone || row.whatsappNumber}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("chatbotv2.attr.platform"),
                    field: "platform",
                    key: "platform",
                    width: 100,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div v-tippy={{ content: row.platform[0].toUpperCase() + row.platform.slice(1) }}>
                                <PlatformIconViewer platform={row.platform} />
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("chatbotv2.attr.last_seen"),
                    field: "_id",
                    key: "_id",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        const formatedDate = this.formatDate(row.connectionTime);
                        return <div v-tippy={{ content: formatedDate }}>{formatedDate}</div>;
                    },
                },
                {
                    title: this.$t("message.firstSeen"),
                    field: "createdAt",
                    key: "createdAt",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        const formatedDate = this.formatDate(row.createdAt);
                        return <div v-tippy={{ content: formatedDate }}>{formatedDate}</div>;
                    },
                },
                {
                    title: this.$t("message.country"),
                    field: "country",
                    key: "country",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.country }}>{row.country}</div>;
                    },
                },
                {
                    title: this.$t("message.city"),
                    field: "city",
                    key: "city",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.city }}>{row.city}</div>;
                    },
                },
                {
                    title: this.$t("message.os"),
                    field: "os",
                    key: "os",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.os }}>{row.os}</div>;
                    },
                },
                {
                    title: this.$t("message.connectionDate"),
                    field: "connectionTime",
                    key: "connectionTime",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        const formatedDate = this.formatDate(row.connectionTime);
                        return <div v-tippy={{ content: formatedDate }}>{formatedDate}</div>;
                    },
                },
                {
                    title: this.$t("message.visitingPage"),
                    field: "visitingPage",
                    key: "visitingPage",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.visitingPage }}>{row.visitingPage}</div>;
                    },
                },
                {
                    title: this.$t("message.browser"),
                    field: "browser",
                    key: "browser",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div v-tippy={{ content: row.browser }}>{row.browser}</div>;
                    },
                },
                {
                    title: "Tags",
                    field: "tags",
                    key: "tags",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div v-tippy={{ content: row.tags.map((tag) => tag.name).join(",") }}>
                                {row.tags.map((tag) => {
                                    return (
                                        <a-tag color={"#22272d"} key={tag.id}>
                                            {tag.name}
                                        </a-tag>
                                    );
                                })}
                            </div>
                        );
                    },
                },
                {
                    title: "Geldiği Reklamlar",
                    field: "metaReferrals",
                    key: "metaReferrals",
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        const referrals = row.metaReferrals;
                        if (!referrals || referrals.length === 0) return <span>-</span>;

                        const mainReferral = referrals[0]; // İlk referral'ı ana olarak göster
                        const hasMultiple = referrals.length > 1;

                        const self = this;
                        return (
                            <div>
                                <a-popover
                                    title={mainReferral.headline || "Reklam Detayları"}
                                    trigger="hover"
                                    placement="bottomRight"
                                    overlayClassName="referral-details-popover"
                                    scopedSlots={{
                                        content: () => (
                                            <div style="max-width: 300px;">
                                                <p>
                                                    <strong>Kaynak Türü:</strong> {mainReferral.sourceType}
                                                </p>
                                                <p>
                                                    <strong>Kaynak ID:</strong> {mainReferral.sourceId}
                                                </p>
                                                {mainReferral.sourceLink && (
                                                    <p>
                                                        <strong>Link:</strong>{" "}
                                                        <a href={mainReferral.sourceLink} target="_blank">
                                                            {mainReferral.sourceLink}
                                                        </a>
                                                    </p>
                                                )}
                                                {mainReferral.body && (
                                                    <p>
                                                        <strong>İçerik:</strong> {mainReferral.body}
                                                    </p>
                                                )}
                                                <p>
                                                    <strong>Tarih:</strong> {self.formatDate(mainReferral.createdAt)}
                                                </p>
                                            </div>
                                        ),
                                    }}
                                >
                                    <a-tag color="#1890ff" style="cursor: pointer;">
                                        {mainReferral.headline || mainReferral.sourceType}
                                    </a-tag>
                                </a-popover>
                                {hasMultiple && (
                                    <a-popover
                                        title="Diğer Reklamlar"
                                        trigger="hover"
                                        placement="bottomRight"
                                        overlayClassName="referral-details-popover"
                                        scopedSlots={{
                                            content: () => (
                                                <div style="max-width: 400px;">
                                                    {referrals.slice(1).map((referral, index) => (
                                                        <div
                                                            key={index}
                                                            style="margin-bottom: 12px; padding-bottom: 8px; border-bottom: index < referrals.length - 2 ? '1px solid #f0f0f0' : 'none'"
                                                        >
                                                            <h4>{referral.headline || referral.sourceType}</h4>
                                                            <p>
                                                                <strong>Kaynak Türü:</strong> {referral.sourceType}
                                                            </p>
                                                            <p>
                                                                <strong>Kaynak ID:</strong> {referral.sourceId}
                                                            </p>
                                                            {referral.sourceLink && (
                                                                <p>
                                                                    <strong>Link:</strong>{" "}
                                                                    <a href={referral.sourceLink} target="_blank">
                                                                        {referral.sourceLink}
                                                                    </a>
                                                                </p>
                                                            )}
                                                            {referral.body && (
                                                                <p>
                                                                    <strong>İçerik:</strong> {referral.body}
                                                                </p>
                                                            )}
                                                            <p>
                                                                <strong>Tarih:</strong>{" "}
                                                                {self.formatDate(referral.createdAt)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ),
                                        }}
                                    >
                                        <a-tag color="#52c41a" style="cursor: pointer; margin-left: 4px;">
                                            +{referrals.length - 1} daha
                                        </a-tag>
                                    </a-popover>
                                )}
                            </div>
                        );
                    },
                },
            ],
            allColumns: [],
            columns: [],
            filterTimeout: null,
            paddingReseted: false,
            tableOptions: {
                rowStyleOption: {
                    stripe: true,
                },
                borderX: true,
                borderY: true,
                scrollWidth: 1130,
                virtualScrollOptions: { itemSize: 50, height: 500 },
                eventCustomOption: {
                    bodyRowEvents: ({ row, rowIndex }) => {
                        return {
                            dblclick: (event) => {
                                if (!this.visitorDetailPermission) {
                                    this.$message.error(this.$t("permissionErrors.visitorDetailRead"));
                                } else {
                                    this.addNewWindow({
                                        type: "show-visitor",
                                        title: `${row.fullname || "Visitor"} Profile`,
                                        payload: {
                                            visitorId: row._id,
                                        },
                                        footer: false,
                                    });
                                }
                            },
                        };
                    },
                },
            },
            paginationCursor: {
                perPage: this.$route.query.perPage ? parseInt(this.$route.query.perPage) : 10,
                total: 0,
                nextToken: null,
                prevToken: null,
                firstPageToken: null,
            },
            selectedFavFilter: visitorStorage.selectedFavFilter,
            selectedChannel: null,
            tableLoadingInstance: null,
            visitorDownloding: false,
            saveAsCustomerGroupModalVisible: false,
            customerGroupName: "",
            importContactsModalVisible: false,
            importContactsType: "multiple", // 'multiple' or 'single'
            importStatus: null,

            // Export state
            showExportSettings: false,
            exportInProgress: false,
            exportProgress: {
                fetched: 0,
                total: 0,
                percent: 0,
            },
            exportCancel: null,
            lastExportSettings: null,

            cancels: [],
            channelContactsList: [
                { key: "whatsapp", platform: "whatsapp", labelKey: "whatsappContacts" },
                { key: "instagram", platform: "instagram", labelKey: "instagramContacts" },
                { key: "messenger", platform: "messenger", labelKey: "messengerContacts" },
                { key: "linkedin", platform: "linkedin", labelKey: "linkedinContacts" },
                { key: "telegram", platform: "telegram", labelKey: "telegramContacts" },
                { key: "metaLeads", platform: "meta-leads", labelKey: "metaLeadsContacts" },
            ],
        };
    },
    computed: {
        ...mapState({
            onlyAdminsShowsNoOwnerContacts: (state) => state.setting.settings.onlyAdminsShowsNoOwnerContacts,
            visitorAttributes: (state) => state.contactProperty.data,
            tags: (state) => state.tag.tags.data,
            hashTags: (state) =>
                state.tag.tags.data.reduce((acc, tag) => {
                    acc[tag._id] = tag;
                    return acc;
                }, {}),
            users: (state) => state.user.users.data,
            segments: (state) => state.CB.segments,
            favFilters: (state) => state.favFilters.filters,
            maskSensitiveInfo: (state) => state.setting.settings.maskSensitiveInfo,
            isRoot: (state) => _.get(state, "auth.user.role.isRoot", false),
            channels: (state) => state.channel.channels || [],
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        searchFieldOptionsWithIcons() {
            return [
                { label: this.$t("visitor.searchFieldAll"), value: "all", icon: "search" },
                { label: this.$t("visitor.searchFieldName"), value: "fullname", icon: "person" },
                { label: this.$t("visitor.searchFieldEmail"), value: "email", icon: "email" },
                { label: this.$t("visitor.searchFieldPhone"), value: "phone", icon: "phone" },
                { label: this.$t("visitor.searchFieldWhatsapp"), value: "whatsappNumber", icon: "comment" },
            ];
        },
        placeholderText() {
            const labels = {
                all: this.$t("visitor.searchPlaceholderAll"),
                fullname: this.$t("visitor.searchPlaceholderFullname"),
                email: this.$t("visitor.searchPlaceholderEmail"),
                phone: this.$t("visitor.searchPlaceholderPhone"),
                whatsappNumber: this.$t("visitor.searchPlaceholderWhatsapp"),
            };
            return labels[this.searchField] || this.$t("visitor.searchPlaceholderDefault");
        },
        visitorDetailPermission() {
            return [
                PERMISSION_TYPES["visitor.delete"].tr.name,
                PERMISSION_TYPES["visitor.update"].tr.name,
                PERMISSION_TYPES["visitor.block"].tr.name,
                PERMISSION_TYPES["visitor.unblock"].tr.name,
            ].some((permission) => this.checkPermission(permission));
        },
        tableColumns() {
            if (this.pickerMode) {
                const selectColumn = {
                    title: "Select",
                    field: "_select",
                    key: "_select",
                    width: 100,
                    align: "center",
                    fixed: "left",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-button type="primary" size="small" onClick={() => this.$emit("select", row)}>
                                Select
                            </a-button>
                        );
                    },
                };
                return [selectColumn, ...this.columns];
            }
            return this.columns;
        },
        visitorFields() {
            const fields = [
                { _id: "fullname", name: "Ad Soyad", valueType: "string" },
                { _id: "email", name: "E-posta", valueType: "string" },
                { _id: "phone", name: "Telefon", valueType: "string" },
                { _id: "platform", name: "Platform", valueType: "string" },
                { _id: "lastSeen", name: "Son Görülme", valueType: "date" },
                { _id: "firstSeen", name: "İlk Görülme", valueType: "date" },
                { _id: "country", name: "Ülke", valueType: "string" },
                { _id: "city", name: "Şehir", valueType: "string" },
                { _id: "os", name: "İşletim Sistemi", valueType: "string" },
                { _id: "browser", name: "Tarayıcı", valueType: "string" },
                { _id: "visitingPage", name: "Ziyaret Edilen Sayfa", valueType: "string" },
                { _id: "tags", name: "Etiketler", valueType: "array", fieldType: "tag" },
                { _id: "metaReferrals", name: "Geldiği Reklamlar", valueType: "objectId", fieldType: "referral" },
                { _id: "vipCustomer", name: "VIP Müşteri", valueType: "boolean" },
                { _id: "owner", name: "Sahip", valueType: "objectId", fieldType: "user" },
                { _id: "channel", name: "Kanal", valueType: "objectId", fieldType: "channel" },
            ];

            // Visitor attributes ekle
            if (this.visitorAttributes) {
                this.visitorAttributes.forEach((attr) => {
                    fields.push({
                        _id: `contactProperty_${attr.key}`,
                        name: attr.name,
                        valueType: attr.type === "number" ? "number" : "string",
                    });
                });
            }

            return fields;
        },
        previewRecords() {
            // Export preview için unwinded visitors
            return this.unwindVisitorsWithReferrals(this.visitors.slice(0, 10));
        },
    },
    methods: {
        ...mapActions({
            getVisitorList: "visitor/getVisitorList",
            downloadVisitorList: "visitor/downloadVisitorList",
            setVisitorDetailPopup: "app/setVisitorDetailPopup",
            createFilter: "favFilters/createFilter",
            fetchFilters: "favFilters/fetchFilters",
            getFavFilters: "favFilters/getFilters",
            updateFilter: "favFilters/updateFilter",
            favFilterDelete: "favFilters/deleteFilter",
            addNewWindow: "app/addNewWindow",
        }),
        changePage(key) {
            this.currentPage = key;
            this.$router.push({ query: { tab: key } });
        },
        getSelectedFilters() {
            return this.filteredItems.map((filter) => {
                return {
                    key: filter.key,
                    rule: filter.rule,
                    input: filter.input,
                    value: filter.value,
                };
            });
        },
        paginationCursorChanged(cursor) {
            // nextToken/prevToken mantığında cursor değişti
            if (cursor.action === "next") {
                this.paginationCursor.prevToken = null;
                this.getVisitors("Watch.paginationCursorChanged.next");
            } else if (cursor.action === "prev") {
                this.paginationCursor.nextToken = null;
                this.getVisitors("Watch.paginationCursorChanged.prev");
            } else if (cursor.action === "perPageChanged") {
                this.paginationCursor.perPage = cursor.perPage;
                this.paginationCursor.nextToken = null;
                this.paginationCursor.prevToken = null;
                this.paginationCursor.firstPageToken = null;
                this.getVisitors("Watch.paginationCursorChanged.perPage");
            }
        },
        formatDate(date) {
            return this.$moment(date).format("LLL");
        },
        selectFavFilter(filter, force = false) {
            this.isSelectingFavFilter = true;
            this.selectedFavFilter = force ? filter : filter._id !== this.selectedFavFilter?._id && filter;
            const filters = getOrUpdateFilters(visitorStorageId);
            this.filteredItems = _.cloneDeep(this.selectedFavFilter?.filters || filters.filters || this.filteredItems);
            
            // Watcher debounce süresi 800ms, o tamamlandıktan sonra flag'i temizle
            clearTimeout(this._favFilterTimeout);
            this._favFilterTimeout = setTimeout(() => {
                this.isSelectingFavFilter = false;
            }, 1200);
        },
        saveFilter(name, item) {
            let isEqualName = false;
            if (!item) {
                item = (this.favFilters["page.contact-list"] || []).find((i) => i.name === name);
                isEqualName = true;
            }
            if (item) {
                this.updateFilter({
                    id: item._id,
                    key: "page.contact-list",
                    name,
                    filters: isEqualName ? this.getSelectedFilters() : item.filters,
                }).then((filter) => {
                    if (item._id === this.selectedFavFilter?._id || isEqualName) {
                        this.selectFavFilter(filter, true);
                    }
                });
            } else {
                this.createFilter({
                    key: "page.contact-list",
                    name,
                    filters: this.getSelectedFilters(),
                }).then((createdItem) => {
                    this.selectFavFilter(createdItem);
                });
            }
        },
        deleteFavFilter(key, id) {
            if (id === this.selectedFavFilter?._id) {
                this.selectFavFilter(this.selectedFavFilter);
            }
            this.favFilterDelete({ key, id });
        },
        deleteFilter(index) {
            this.filteredItems = this.filteredItems.filter((filter, i) => i !== index);
        },
        onChangeCol(col, e) {
            if (e.target.checked) {
                this.columns = [...this.columns, col];
            } else {
                this.columns = this.columns.filter((c) => c.key !== col.key);
            }
            localStorage.setItem("contacts-columns", JSON.stringify(this.columns));
        },
        syncColumns() {
            const columns = JSON.parse(
                localStorage.getItem("contacts-columns") || JSON.stringify(this.staticColumns.slice(0, 5))
            );

            const columnsHash = columns.reduce((acc, column) => {
                acc[column.key] = true;
                return acc;
            }, {});

            this.allColumns = [
                ...this.staticColumns.map((col) => ({
                    ...col,
                    enabled: columnsHash[col.key] || false,
                    width: 200,
                    align: "left",
                })),
                ...this.$_.cloneDeep(this.visitorAttributes).map((attr) => ({
                    title: attr.label,
                    field: `contactProperty_${attr._id}`,
                    key: `contactProperty_${attr._id}`,
                    enabled: columnsHash[`contactProperty_${attr._id}`] || false,
                    width: 200,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        const val = this.getContactPropertyValue(row, attr._id);
                        return <div v-tippy={{ content: val }}>{val}</div>;
                    },
                })),
            ];
            const hashedColumns = this.allColumns.reduce((acc, col) => {
                acc[col.key] = col;
                return acc;
            }, {});

            this.columns = columns.map((col) => hashedColumns[col.key]);
        },
        clearFilters() {
            this.filteredItems = [];
        },
        syncFilters() {
            const findPropertyFilter = this.filters.findIndex((f) => f.key === "contact_properties");
            const propertyFilter = {
                type: "contact-property-selector",
                name: this.$t("message.contactProperties"),
                material_icons: "alternate_email",
                multiple: true,
                key: "contact_properties",
                operators: true,
                optionSelect: true,
                options: this.visitorAttributes.map((attr) => ({
                    key: attr._id,
                    value: attr.label,
                    type: attr.type,
                })),
            };
            if (findPropertyFilter < 0) {
                this.filters = [...this.filters, propertyFilter];
            } else {
                this.filters.splice(findPropertyFilter, 1, propertyFilter);
            }

            const findTagFilter = this.filters.findIndex((f) => f.key === "tags");
            const tagFilter = {
                type: "select",
                name: this.$t("visitor.tags"),
                material_icons: "label",
                multiple: true,
                key: "tags",
                operators: true,
                defaultValue: [],
                omitOperators: [
                    "starts_with",
                    "ends_with",
                    "less_than",
                    "less_or_equal",
                    "greater_than",
                    "greater_or_equal",
                ],
                options: this.tags.map((tag) => ({
                    key: tag._id,
                    value: tag.name,
                })),
            };
            if (findTagFilter < 0) {
                this.filters = [...this.filters, tagFilter];
            } else {
                this.filters.splice(findTagFilter, 1, tagFilter);
            }

            const fUserFilter = this.filters.findIndex((f) => f.key === "preferredAgents");
            const userFilter = {
                type: "select",
                name: this.$t("message.prefferedAgents"),
                material_icons: "support_agent",
                multiple: true,
                key: "preferredAgents",
                operators: true,
                omitOperators: [
                    "starts_with",
                    "ends_with",
                    "less_than",
                    "less_or_equal",
                    "greater_than",
                    "greater_or_equal",
                ],
                options: this.users.map((user) => ({
                    key: user._id,
                    value: user.name,
                })),
            };
            if (fUserFilter < 0) {
                this.filters = [...this.filters, userFilter];
            } else {
                this.filters.splice(fUserFilter, 1, userFilter);
            }

            const fOwnerFilter = this.filters.findIndex((f) => f.key === "owner");
            let ownerOptions = [
                {
                    key: "unowned",
                    value: "Sahipsiz",
                },
                ...this.users.map((user) => ({
                    key: user._id,
                    value: user.name,
                })),
            ];
            if (!this.isRoot) {
                const onlyAdminsShowsNoOwnerContacts = this.onlyAdminsShowsNoOwnerContacts;
                if (onlyAdminsShowsNoOwnerContacts) {
                    ownerOptions = ownerOptions.filter((user) => user.key !== "unowned");
                }

                const currentUserId = _.get(this.$store.state.auth.user, "_id", null);
                if (currentUserId) {
                    ownerOptions = ownerOptions.filter((option) => option.key === currentUserId);
                }
            }

            const ownerFilter = {
                type: "select",
                name: this.$t("visitor.ownership"),
                material_icons: "assignment_ind",
                multiple: true,
                key: "owner",
                operators: true,
                omitOperators: [
                    "starts_with",
                    "ends_with",
                    "less_than",
                    "less_or_equal",
                    "greater_than",
                    "greater_or_equal",
                ],
                options: ownerOptions,
            };
            if (fOwnerFilter < 0) {
                this.filters = [...this.filters, ownerFilter];
            } else {
                this.filters.splice(fOwnerFilter, 1, ownerFilter);
            }

            // const fSegmentFilter = this.filters.findIndex((f) => f.key === "segment");
            // const segmentFilter = {
            //     type: "select",
            //     name: this.$t("visitor.segments"),
            //     material_icons: "groups",
            //     multiple: true,
            //     key: "segment",
            //     operators: true,
            //     omitOperators: [
            //         "starts_with",
            //         "ends_with",
            //         "less_than",
            //         "less_or_equal",
            //         "greater_than",
            //         "greater_or_equal",
            //     ],
            //     options: this.segments.map((user) => ({
            //         key: user._id,
            //         value: user.name,
            //     })),
            // };
            // if (fSegmentFilter < 0) {
            //     this.filters = [...this.filters, segmentFilter];
            // } else {
            //     this.filters.splice(fSegmentFilter, 1, segmentFilter);
            // }

            // Channel filter ekle
            const fChannelFilter = this.filters.findIndex((f) => f.key === "channel");
            const channelFilter = {
                type: "select",
                name: "Geldiği Kanal",
                material_icons: "hub",
                multiple: true,
                key: "channel",
                operators: true,
                omitOperators: [
                    "starts_with",
                    "ends_with",
                    "less_than",
                    "less_or_equal",
                    "greater_than",
                    "greater_or_equal",
                ],
                options: this.channels
                    .filter((ch) => !ch.isDeleted)
                    .map((channel) => ({
                        key: channel._id,
                        value: channel.name,
                    })),
            };
            if (fChannelFilter < 0) {
                this.filters = [...this.filters, channelFilter];
            } else {
                this.filters.splice(fChannelFilter, 1, channelFilter);
            }
        },
        getContactPropertyValue(visitor, cpId) {
            return visitor.contactProperties?.find((c) => c.property === cpId)?.value || "N/A";
        },
        paddingReset() {
            this.paddingReseted = !this.paddingReseted;
        },
        onSearch: _.debounce(function () {
            this.paginationCursor.nextToken = null;
            this.paginationCursor.prevToken = null;
            this.paginationCursor.firstPageToken = null;
            this.getVisitors("Watch.onSearch");
        }, 500),
        onSearchFieldChange() {
            this.paginationCursor.nextToken = null;
            this.paginationCursor.prevToken = null;
            this.paginationCursor.firstPageToken = null;
            this.getVisitors("SearchFieldChange");
        },
        handleSearchInput() {
            const query = this.searchText ? this.searchText.trim() : '';
            
            if (!query) {
                // Query yoksa popover'ı kapat
                if (this.detectedSearchMode) {
                    this.searchModePopoverVisible = false;
                    this.detectedSearchMode = null;
                    this.pendingSearchQuery = null;
                }
                this.onSearch();
                return;
            }
            
            // Eğer zaten bir detection varsa ve query değişmediyse, popover'ı açık tut
            if (this.detectedSearchMode && this.pendingSearchQuery === query) {
                return;
            }
            
            const isEmailResult = this.isEmail(query);
            const isPhoneResult = this.isPhoneNumber(query);
            
            // 1. Herhangi bir alanda email yazıyorsa VE email alanında değilse → email suggestion göster
            if (isEmailResult && this.searchField !== 'email') {
                this.detectedSearchMode = 'email';
                this.pendingSearchQuery = query;
                this.searchModePopoverVisible = true;
                return;
            }
            
            // 2. Herhangi bir alanda telefon yazıyorsa VE telefon alanında değilse → telefon suggestion göster
            if (isPhoneResult && this.searchField !== 'phone') {
                this.detectedSearchMode = 'phone';
                this.pendingSearchQuery = query;
                this.searchModePopoverVisible = true;
                return;
            }
            
            // 3. Telefon alanında VE telefon değilse VE email değilse → "tüm alanlarda ara/isim'de ara" suggestion göster
            if (this.searchField === 'phone' && !isPhoneResult && !isEmailResult) {
                this.detectedSearchMode = 'not_phone';
                this.pendingSearchQuery = query;
                this.searchModePopoverVisible = true;
                return;
            }
            
            // 4. Email alanında VE email değilse VE telefon değilse → "tüm alanlarda ara/isim'de ara" suggestion göster
            if (this.searchField === 'email' && !isEmailResult && !isPhoneResult) {
                this.detectedSearchMode = 'not_email';
                this.pendingSearchQuery = query;
                this.searchModePopoverVisible = true;
                return;
            }
            
            // Normal arama yap
            if (this.detectedSearchMode) {
                this.searchModePopoverVisible = false;
                this.detectedSearchMode = null;
                this.pendingSearchQuery = null;
            }
            this.onSearch();
        },
        isPhoneNumber(value) {
            const digitCount = (value.match(/\d/g) || []).length;
            if (digitCount < 8) return false;

            const phonePattern = /^[\+]?[\d\s\-\(\)\.]+$/;
            if (!phonePattern.test(value)) return false;

            if (value.includes('@')) return false;
            if (value.includes('http')) return false;

            return true;
        },
        isEmail(value) {
            if (!value || typeof value !== 'string') {
                return false;
            }
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailPattern.test(value);
        },
        getSearchModeIcon() {
            if (this.detectedSearchMode === 'phone') return 'phone';
            if (this.detectedSearchMode === 'email') return 'email';
            if (this.detectedSearchMode === 'not_phone') return 'phone_disabled';
            if (this.detectedSearchMode === 'not_email') return 'email';
            return 'info';
        },
        getSearchModeTitle() {
            if (this.detectedSearchMode === 'phone') return this.$t('visitor.searchModePhoneDetected');
            if (this.detectedSearchMode === 'email') return this.$t('visitor.searchModeEmailDetected');
            if (this.detectedSearchMode === 'not_phone') return this.$t('visitor.searchModeNotPhone');
            if (this.detectedSearchMode === 'not_email') return this.$t('visitor.searchModeNotEmail');
            return this.$t('visitor.searchMode');
        },
        getSearchModeMessage() {
            const fieldName = this.searchField === 'fullname' || this.searchField === 'name' ? this.$t('visitor.searchFieldName') : this.$t('visitor.searchFieldAll');
            if (this.detectedSearchMode === 'phone') {
                return this.$t('visitor.searchModePhoneMessage', { query: this.pendingSearchQuery, field: fieldName });
            }
            if (this.detectedSearchMode === 'email') {
                return this.$t('visitor.searchModeEmailMessage', { query: this.pendingSearchQuery, field: fieldName });
            }
            if (this.detectedSearchMode === 'not_phone') {
                return this.$t('visitor.searchModeNotPhoneMessage', { query: this.pendingSearchQuery });
            }
            if (this.detectedSearchMode === 'not_email') {
                return this.$t('visitor.searchModeNotEmailMessage', { query: this.pendingSearchQuery });
            }
            return '';
        },
        confirmSearchMode() {
            if (this.detectedSearchMode === 'phone') {
                this.searchField = 'phone';
            } else if (this.detectedSearchMode === 'email') {
                this.searchField = 'email';
            }
            
            let cleanedQuery = this.pendingSearchQuery;
            if (this.detectedSearchMode === 'phone') {
                cleanedQuery = cleanedQuery.replace(/\D/g, '');
            }
            
            this.searchText = cleanedQuery;
            this.searchModePopoverVisible = false;
            this.detectedSearchMode = null;
            this.pendingSearchQuery = null;
            
            this.paginationCursor.nextToken = null;
            this.paginationCursor.prevToken = null;
            this.paginationCursor.firstPageToken = null;
            this.getVisitors("ConfirmSearchMode");
        },
        switchToAllField() {
            this.searchField = 'all';
            this.searchText = this.pendingSearchQuery;
            this.searchModePopoverVisible = false;
            this.detectedSearchMode = null;
            this.pendingSearchQuery = null;
            
            this.paginationCursor.nextToken = null;
            this.paginationCursor.prevToken = null;
            this.paginationCursor.firstPageToken = null;
            this.getVisitors("SwitchToAllField");
        },
        switchToFullnameField() {
            this.searchField = 'fullname';
            this.searchText = this.pendingSearchQuery;
            this.searchModePopoverVisible = false;
            this.detectedSearchMode = null;
            this.pendingSearchQuery = null;
            
            this.paginationCursor.nextToken = null;
            this.paginationCursor.prevToken = null;
            this.paginationCursor.firstPageToken = null;
            this.getVisitors("SwitchToFullnameField");
        },
        cancelSearchMode() {
            this.searchModePopoverVisible = false;
            this.detectedSearchMode = null;
            this.pendingSearchQuery = null;
            // Query'yi olduğu gibi bırak, sadece popover'ı kapat
            // Normal search yap (mevcut field'da)
            this.onSearch();
        },
        dismissSearchSuggestion() {
            this.searchModePopoverVisible = false;
            this.detectedSearchMode = null;
            this.pendingSearchQuery = null;
        },
        clearSearch() {
            this.searchText = "";
            this.searchModePopoverVisible = false;
            this.detectedSearchMode = null;
            this.pendingSearchQuery = null;
            this.paginationCursor.nextToken = null;
            this.paginationCursor.prevToken = null;
            this.paginationCursor.firstPageToken = null;
            this.getVisitors("ClearSearch");
        },
        isMaskContactInfo,
        isShouldMask(visitor, val) {
            return (
                !this.isRoot &&
                this.maskSensitiveInfo &&
                (val === visitor.email || val === visitor.whatsappNumber || val === visitor.phone)
            );
        },
        maskContactInfo(visitor, key, length = 4) {
            return this.isMaskContactInfo(key)
                ? maskContactInfo(visitor[key], length)
                : this.isShouldMask(visitor, visitor[key])
                ? maskContactInfo(visitor[key], length)
                : visitor[key];
        },
        unwindVisitorsWithReferrals(visitors) {
            const unwoundVisitors = [];

            visitors.forEach((visitor) => {
                if (visitor.metaReferrals && visitor.metaReferrals.length > 0) {
                    // Her referral için ayrı satır oluştur
                    visitor.metaReferrals.forEach((referral) => {
                        unwoundVisitors.push({
                            ...visitor,
                            _currentReferral: referral,
                            _originalId: visitor._id, // Orijinal visitor id'sini sakla
                            _id: `${visitor._id}_${referral.sourceId}_${Date.parse(referral.createdAt)}`, // Unique id oluştur
                        });
                    });
                } else {
                    // Referral yoksa normal satır ekle
                    unwoundVisitors.push({
                        ...visitor,
                        _currentReferral: null,
                        _originalId: visitor._id,
                    });
                }
            });

            return unwoundVisitors;
        },
        getVisitors(source) {
            this.tableLoadingInstance.show();
            const filters = this.getSelectedFilters();
            getOrUpdateFilters(visitorStorageId, {
                filters,
                selectedFavFilter: this.selectedFavFilter,
                query: this.searchText,
                searchField: this.searchField,
            });
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    perPage: this.paginationCursor.perPage,
                },
            });

            this.getVisitorList({
                filters,
                pageSize: this.paginationCursor.perPage,
                query: this.searchText,
                searchField: this.searchField,
                nextToken: this.paginationCursor.nextToken,
                prevToken: this.paginationCursor.prevToken,
                firstPageToken: this.paginationCursor.firstPageToken,
            }).then((response) => {
                this.tableLoadingInstance.close();
                
                // Pagination tokens güncelle
                this.paginationCursor.nextToken = response.data.cursor?.nextToken || null;
                this.paginationCursor.prevToken = response.data.cursor?.prevToken || null;
                this.paginationCursor.firstPageToken =
                    response.data.cursor?.firstPageToken || this.paginationCursor.firstPageToken;
                if (response.data.total) {
                    this.paginationCursor.total = response.data.total;
                }

                this.visitors = response.data.visitors.map((visitor) => {
                    return {
                        ...visitor,
                        fullname: this.maskContactInfo(visitor, "fullname"),
                        email: this.maskContactInfo(visitor, "email"),
                        phone: this.maskContactInfo(visitor, "phone"),
                        whatsappNumber: this.maskContactInfo(visitor, "whatsappNumber"),
                    };
                });
            });
        },
        async exportToCSV() {
            const data = this.visitors.map((visitor) => {
                return this.columns.reduce((acc, column) => {
                    let value = visitor[column.key];
                    if (column.key === "tags") {
                        const names = visitor.tags.map((tag) =>
                            this.hashTags[tag] ? this.hashTags[tag].name : this.$t("visitor.unknownTag")
                        );
                        value = names.join(",");
                    } else if (column.key === "connectionTime") {
                        value = this.formatDate(this.connectionTime);
                    } else if (column.key === "createdAt") {
                        value = this.formatDate(this.createdAt);
                    } else if (column.key === "_id") {
                        value = this.formatDate(this.connectionTime);
                    } else if (column.key.startsWith("contactProperty_")) {
                        value = this.getContactPropertyValue(visitor, column.key.split("_")[1]);
                    }
                    acc[column.title] = value;
                    return acc;
                }, {});
            });
            exportToCSV(data, `Supsis Contact List (${this.$moment().format("YYYY-MM-DD HH:MM")})`);

            // Activity log
            try {
                await this.$store.dispatch("activity/logDownloadActivity", {
                    activityType: "CONTACT_DATA_DOWNLOADED",
                    data: {
                        contactCount: data.length,
                        format: "csv",
                    },
                });
            } catch (error) {
                console.error("Activity log error:", error);
            }
        },
        downloadAll() {
            if (this.visitorDownloding || this.exportInProgress) return;
            this.showExportSettings = true;
        },
        closeExportSettings() {
            this.showExportSettings = false;
        },
        async onExportSettingsStart(settings) {
            this.lastExportSettings = settings;
            this.showExportSettings = false;

            // Modal'ın kapanmasını bekle
            await this.$nextTick();

            await this.startFullExport(settings);
        },
        async startFullExport(settings) {
            try {
                const perPage = 200;
                const total = this.paginationCursor.total || 0;
                if (!total) {
                    this.$message.info(this.$t("visitor.noRecordsToDownload"));
                    return;
                }

                // Progress başlat
                this.exportInProgress = true;
                this.exportProgress = { fetched: 0, total: total, percent: 0 };
                this.exportCancel = { cancelled: false };

                // DOM güncellemesi
                await this.$nextTick();

                const all = [];
                let currentNextToken = null;
                let fetchedCount = 0;

                while (fetchedCount < total) {
                    if (this.exportCancel && this.exportCancel.cancelled) break;

                    const result = await this.getVisitorList({
                        pageSize: perPage,
                        filters: this.getSelectedFilters(),
                        query: this.searchText,
                        searchField: this.searchField,
                        nextToken: currentNextToken,
                    });

                    const items = (result && result.data && result.data.visitors) || [];
                    // Export için unwinding yap
                    const unwoundItems = this.unwindVisitorsWithReferrals(items);
                    for (let i = 0; i < unwoundItems.length; i++) all.push(unwoundItems[i]);

                    fetchedCount += items.length;
                    currentNextToken = result.data.cursor?.nextToken || null;

                    // Progress güncelle
                    const percent = Math.floor((fetchedCount / total) * 100);
                    this.exportProgress = { fetched: fetchedCount, total: total, percent: percent };

                    // UI güncellemesi
                    await this.$nextTick();

                    // NextToken yoksa bitir
                    if (!currentNextToken) {
                        break;
                    }
                }

                if (!this.exportCancel || !this.exportCancel.cancelled) {
                    const excelData = this.buildExcelData(all, settings);
                    expertToExcel(excelData, `visitors.xlsx`, "visitors");
                    this.$message.success(this.$t("visitor.excelCreatedSuccess"));

                    // Activity log
                    try {
                        await this.$store.dispatch("activity/logDownloadActivity", {
                            activityType: "CONTACT_DATA_DOWNLOADED",
                            data: {
                                contactCount: excelData.length,
                                format: "excel",
                                totalRecords: total,
                            },
                        });
                    } catch (error) {
                        console.error("Activity log error:", error);
                    }
                } else {
                    this.$message.info(this.$t("visitor.exportCancelled"));
                }
            } catch (e) {
                console.error("Full export error", e);
                this.$message.error(this.$t("visitor.excelCreateError"));
            } finally {
                this.exportInProgress = false;
                this.exportCancel = null;
            }
        },
        cancelExport() {
            if (this.exportCancel) this.exportCancel.cancelled = true;
        },
        buildExcelData(visitors, settings) {
            const cols = (settings && settings.columns) || [];
            const rows = [];
            for (let r = 0; r < visitors.length; r++) {
                const visitor = visitors[r];
                const row = {};
                for (let s = 0; s < cols.length; s++) {
                    const col = cols[s];
                    this.appendVisitorColumn(row, visitor, col);
                }
                rows.push(row);
            }
            return rows;
        },
        appendVisitorColumn(row, visitor, col) {
            try {
                let colBase = col.label;
                const fieldId = col.fieldId;
                let value = "";

                // Visitor özelliklerine göre değer al
                switch (fieldId) {
                    case "fullname":
                        value = visitor.fullname || "";
                        break;
                    case "email":
                        value = visitor.email || "";
                        break;
                    case "phone":
                        value = visitor.phone || visitor.whatsappNumber || "";
                        break;
                    case "platform":
                        value = visitor.platform || "";
                        break;
                    case "lastSeen":
                        value = visitor.connectionTime
                            ? moment(visitor.connectionTime).format(col.dateFormat || "YYYY-MM-DD HH:mm")
                            : "";
                        break;
                    case "firstSeen":
                        value = visitor.createdAt
                            ? moment(visitor.createdAt).format(col.dateFormat || "YYYY-MM-DD HH:mm")
                            : "";
                        break;
                    case "country":
                        value = visitor.country || "";
                        break;
                    case "city":
                        value = visitor.city || "";
                        break;
                    case "os":
                        value = visitor.os || "";
                        break;
                    case "browser":
                        value = visitor.browser || "";
                        break;
                    case "visitingPage":
                        value = visitor.visitingPage || "";
                        break;
                    case "tags":
                        if (visitor.tags && Array.isArray(visitor.tags)) {
                            const tagProp = col.tagProp || "name";
                            value = visitor.tags
                                .map((tag) => {
                                    return tag && typeof tag === "object" ? tag[tagProp] || "" : tag;
                                })
                                .filter((x) => x != null && x !== "")
                                .join(", ");
                        } else {
                            value = "";
                        }
                        break;
                    case "metaReferrals":
                        if (visitor._currentReferral) {
                            const referralProp = col.referralProp || "headline";
                            const rawValue = visitor._currentReferral[referralProp];
                            // Tarih formatlaması özel durumu
                            if (referralProp === "createdAt" && rawValue) {
                                value = moment(rawValue).format("YYYY-MM-DD HH:mm");
                            } else {
                                value = rawValue || "";
                            }
                        } else {
                            value = "";
                        }
                        break;
                    case "vipCustomer":
                        value = visitor.vipCustomer === true ? this.$t("visitor.yes") : this.$t("visitor.no");
                        break;
                    case "owner":
                        if (visitor.owner && typeof visitor.owner === "object") {
                            value = visitor.owner.name || visitor.owner.email || "";
                        } else {
                            value = this.$t("visitor.unowned");
                        }
                        break;
                    case "channel":
                        if (visitor.channel && typeof visitor.channel === "object") {
                            value = visitor.channel.name || "";
                        } else {
                            value = "";
                        }
                        break;
                    default:
                        // Custom property'ler için
                        if (fieldId.startsWith("contactProperty_")) {
                            const propKey = fieldId.split("_")[1];
                            value = this.getContactPropertyValue(visitor, propKey) || "";
                        } else {
                            value = visitor[fieldId] || "";
                        }
                }

                row[colBase] = value;
            } catch (e) {
                console.error("Error appending column:", e);
                row[col.label] = "";
            }
        },
        showSaveAsCustomerGroupModal(type) {
            this.saveAsCustomerGroupModalVisible = type;
        },
        async saveAsWhatsappGroup() {
            const query = this.searchText;
            const groupName = this.customerGroupName;

            try {
                await WHATSAPP_API.BROADCAST.saveAsWhatsappCustomerGroup(
                    this.siteId,
                    this.getSelectedFilters(),
                    query,
                    groupName
                );
                this.$message.success(this.$t("visitor.whatsappGroupCreatedSuccess"));
            } catch (error) {
                this.$message.error(this.$t("visitor.whatsappGroupCreateError"));
            }

            this.saveAsCustomerGroupModalVisible = null;
            this.customerGroupName = "";
        },
        closeCustomerGroupModal() {
            this.saveAsCustomerGroupModalVisible = null;
            this.customerGroupName = "";
        },
        async saveAsEmailCustomerGroup() {
            const query = this.searchText;
            const groupName = this.customerGroupName;
            try {
                await MAIL_API.BROADCAST.saveAsEmailCustomerGroup(
                    this.siteId,
                    this.getSelectedFilters(),
                    query,
                    groupName
                );
                this.$message.success(this.$t("visitor.mailGroupCreatedSuccess"));
            } catch (error) {
                console.error(error);
                this.$message.error(this.$t("visitor.mailGroupCreateError"));
            }

            this.saveAsCustomerGroupModalVisible = null;
            this.customerGroupName = "";
        },
        async saveCustomerGroup() {
            if (this.saveAsCustomerGroupModalVisible === "whatsapp") {
                this.saveAsWhatsappGroup();
            } else {
                this.saveAsEmailCustomerGroup();
            }
        },
        getEnabledMenuItems() {
            return this.menus.filter((menu) => {
                return menu.permissions.some((permission) => this.checkPermission(permission));
            });
        },
        showImportContactsModal(type) {
            this.importContactsModalVisible = true;
            this.importContactsType = type;
        },
        closeImportContactsModal() {
            this.importContactsModalVisible = false;
        },
        onSingleImportCompleted(result) {
            this.closeImportContactsModal();
            this.getVisitors("Watch.onSingleImportCompleted");
        },
        handleImportContacts(data) {
            this.closeImportContactsModal();
            const { contacts, platform, channels, advancedSettings } = data;
            createMultipleContacts(contacts, platform, channels, advancedSettings)
                .then((response) => {
                    this.importJobId = response.jobId;
                    this.importStatus = {
                        status: "processing",
                        imported: 0,
                        failed: 0,
                        total: response.total,
                        percent: 0,
                    };

                    this.$message.success(`${response.total} kişi içe aktarma işlemi başlatıldı`);
                })
                .catch((error) => {
                    console.error("Kişileri içe aktarma hatası:", error);
                    this.$message.error("Kişileri içe aktarırken bir hata oluştu");
                });
        },
        handleAddContact(contact) {
            createContact(contact)
                .then(() => {
                    this.$message.success("Kişi başarıyla eklendi");
                })
                .catch((error) => {
                    console.error("Kişi ekleme hatası:", error);
                    this.$message.error("Kişi eklenirken bir hata oluştu");
                })
                .finally(() => {
                    this.closeImportContactsModal();
                });
        },
        handleChannelContactClick(channel) {
            this.selectedChannelContactType = channel.key;
            this.channelContactsPopoverVisible = false;
        },
        closeChannelContactsModal() {
            this.selectedChannelContactType = null;
        },
        getChannelContactModalTitle() {
            const keyMap = {
                whatsapp: "whatsappContacts",
                instagram: "instagramContacts",
                messenger: "messengerContacts",
                linkedin: "linkedinContacts",
                telegram: "telegramContacts",
                metaLeads: "metaLeadsContacts",
            };
            const key = keyMap[this.selectedChannelContactType];
            return key ? this.$t("visitor." + key) : this.$t("visitor.channelContacts");
        },
    },
    async mounted() {
        this.tableLoadingInstance = this.$veLoading({
            target: document.querySelector("#supsis-contacts-list"),
            name: "wave",
        });
        this.syncFilters();
        this.syncColumns();
        this.getVisitors("Watch.mounted");
        this.fetchFilters("page.contact-list");

        const enabledMenuItems = this.getEnabledMenuItems();
        const isEnabled = enabledMenuItems.some((menu) => menu.key === this.currentPage);
        if (!isEnabled) {
            this.currentPage = enabledMenuItems[0].key;
        }

        const sub = socketMessageStream
            .pipe(filter((x) => ["contact_import_status"].includes(x.cmd)))
            .subscribe((x) => {
                if (x.cmd === "contact_import_status") {
                    const { status, imported, failed, total, percent } = x.data;
                    this.importStatus = {
                        status,
                        imported,
                        failed,
                        total,
                        percent,
                    };
                }
            });

        this.cancels.push(() => {
            sub.unsubscribe();
        });

        // Search mode suggestion popover dışına tıklandığında kapat
        this._dismissSuggestionHandler = (event) => {
            if (!this.searchModePopoverVisible) return;
            const popoverEl = this.$el.querySelector('.search-mode-suggestion-popover');
            const wrapperEl = this.$el.querySelector('.search-section-wrapper');
            if (popoverEl && !popoverEl.contains(event.target) && wrapperEl && !wrapperEl.contains(event.target)) {
                this.dismissSearchSuggestion();
            }
        };
        document.addEventListener('mousedown', this._dismissSuggestionHandler);
        this.cancels.push(() => {
            document.removeEventListener('mousedown', this._dismissSuggestionHandler);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cb) => cb());
    },
    watch: {
        "$route.query.tab": {
            handler(tab) {
                this.currentPage = tab;
            },
        },
        currentPage: {
            handler() {
                const enabledMenuItems = this.getEnabledMenuItems();
                const isEnabled = enabledMenuItems.some((menu) => menu.key === this.currentPage);
                if (!isEnabled) {
                    this.currentPage = enabledMenuItems[0].key;
                    this.$router.push({ query: { tab: this.currentPage } });
                }
            },
        },
        visitorAttributes: {
            handler() {
                this.syncColumns();
                this.syncFilters();
            },
            deep: true,
        },
        tags: {
            handler() {
                this.syncFilters();
            },
            deep: true,
        },
        users: {
            handler() {
                this.syncFilters();
            },
            deep: true,
        },
        segments: {
            handler() {
                this.syncFilters();
            },
            deep: true,
        },
        channels: {
            handler() {
                this.syncFilters();
            },
            deep: true,
        },
        filteredItems: {
            handler() {
                if (this.filterTimeout) clearTimeout(this.filterTimeout);
                this.filterTimeout = setTimeout(() => {
                    // Eğer favori filtre seçiliyorsa, hash kontrolünü atla
                    if (this.isSelectingFavFilter) {
                        // Token'ları sıfırla
                        this.paginationCursor.nextToken = null;
                        this.paginationCursor.prevToken = null;
                        this.paginationCursor.firstPageToken = null;
                        this.getVisitors("Watch.onFilterChanged");
                        return;
                    }
                    
                    const selectedFilters = StringUtils.hashCodeObj({ ...this.getSelectedFilters() });
                    const selectedFavFilters = StringUtils.hashCodeObj({ 
                        ...(this.selectedFavFilter?.filters || []).map(f => ({
                            field: f.key,
                            value: f.value,
                            operator: f.rule
                        }))
                    });
                    if (this.selectedFavFilter && selectedFilters !== selectedFavFilters) {
                        this.selectedFavFilter = null;
                    }
                    // Token'ları sıfırla
                    this.paginationCursor.nextToken = null;
                    this.paginationCursor.prevToken = null;
                    this.paginationCursor.firstPageToken = null;
                    this.getVisitors("Watch.onFilterChanged");
                }, 800);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.visitor-page {
    display: flex;
    flex-direction: column;

    &-head {
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        min-height: 60px;
        height: 60px;
        max-height: 60px;
        border-bottom: 1px solid var(--color-border-default);
        padding: 10px;
    }

    #banned-visitors,
    #tags,
    #visitor-attributes {
        padding: 15px;
    }
}

.notting-here {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 200px;
    }
}

.download-popover {
    .download-options {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;

        .export-csv-visitor-page {
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            gap: 5px;

            &.disabled {
                cursor: not-allowed;
            }
        }
    }
}

#visitors {
    position: relative;
    height: 100%;
    display: flex;
    max-width: calc(100vw - 75px);

    #supsis-contacts-list {
        .ve-loading-wave-rect {
            background: #22272d !important;
        }
    }

    .visitor-table {
        .ve-table-header-tr,
        .ve-table-body-tr,
        .ve-table-header-th,
        .ve-table-body-td div {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            height: min-content;
        }

        &.paddingReseted {
            .ve-table-header-tr,
            .ve-table-body-tr {
                height: 0;
            }

            .ve-table-header-th,
            .ve-table-body-td {
                padding: 0;
            }
        }
    }

    .visitor-pagination {
        margin-top: 5px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    .flex-center-center {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .padding-zero {
        th,
        td {
            padding: 0;
        }
    }

    .limited-row td {
        max-width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-transform: ellipsis;
        text-overflow: ellipsis;
    }

    .filter-sidebar {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        justify-content: space-between;
        width: 280px;
        max-width: 280px;
        border-right: 1px solid var(--color-border-default);
        position: relative;
        transition: all 0.3s;
        white-space: nowrap;
        font-family: Poppins;

        .filter-sidebar-content {
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            gap: 4px;
            width: 280px;

            .filter-sidebar-search-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                padding: 12px 10px;
                border-bottom: 1px solid var(--color-border-default);
                gap: 8px;

                .search-header-title {
                    font-weight: 600;
                    font-size: 16px;
                    flex: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .anticon {
                    font-size: 18px;
                    cursor: pointer;
                    color: #faad14;
                    flex-shrink: 0;
                    pointer-events: auto;

                    &:hover {
                        color: #d48806;
                    }
                }

                #filter-modal {
                    cursor: pointer;
                    pointer-events: auto;
                    display: inline-block;

                    .filters-content {
                        cursor: pointer;
                        pointer-events: auto;
                        display: inline-block;
                    }
                }
            }

            .filter-sidebar-group {
                border-bottom: 1px solid var(--color-border-default);
                width: 100%;
                padding: 10px;
                display: flex;
                align-items: flex-start;
                flex-direction: column;
                gap: 6px;

                .filter-sidebar-group-title {
                    font-weight: 600;
                    font-size: 14px;
                }

                .filter-sidebar-group-content {
                    width: 100%;

                    .favorite-items {
                        display: flex;
                        align-items: center;
                        flex-direction: column;
                        gap: 4px;
                        width: 100%;

                        .favorite-item {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 3px;
                            width: 100%;
                            padding: 10px 12px;
                            border-radius: 6px;
                            cursor: pointer;
                            transition: all 0.2s ease;
                            background-color: transparent;
                            color: #000;

                            &.selected {
                                background-color: #EEE7FF;
                                color: #5625CC;
                                font-weight: 600;
                            }

                            &:hover {
                                background: #E6D9FF;
                                color: #4a1fb3;
                            }

                            .favorite-item-content {
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                width: 100%;

                                .favorite-item-icon {
                                    &.material-icons {
                                        font-size: 18px;
                                        color: #5625CC;
                                    }

                                    .whatsapp-icon path {
                                        fill: #0bba18;
                                    }
                                }

                                .favorite-item-text {
                                    font-size: 13px;
                                    font-weight: 500;
                                    overflow: hidden;
                                    white-space: nowrap;
                                    text-overflow: ellipsis;
                                    color: #5625CC;
                                }
                            }

                            &:hover {
                                .favorite-item-edit-wrapper {
                                    opacity: 1;
                                }
                            }

                            .favorite-item-edit-wrapper {
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                opacity: 0;
                                transition: opacity 0.2s ease;
                            }

                            .favorite-item-edit {
                                border: 1px solid var(--color-border-default);
                                background: white;
                                color: black;
                                font-size: 18px;
                                font-weight: 400;
                                border-radius: 3px;
                                cursor: pointer;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            }
                        }
                    }
                }
            }
        }

        .filter-sidebar-footer {
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            gap: 4px;
            width: 100%;

            .filter-sidebar-control {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
                border-top: 1px solid var(--color-border-default);
                width: 100%;
                padding: 10px;

                .control-button {
                    width: 100%;
                    border: 1px solid var(--color-border-default);
                    color: var(--default-text-color);
                    padding: 5px 10px;
                    border-radius: 4px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    font-size: 14px;
                    user-select: none;

                    .material-icons {
                        font-size: 16px;
                    }
                }
            }

            .filter-sidebar-collapse {
                border-top: 1px solid var(--color-border-default);
                color: var(--default-text-color);
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                cursor: pointer;
                user-select: none;
                height: 40px;
                padding: 0 10px;
            }
        }

        &.closed {
            width: 50px !important;

            .filter-sidebar-content {
                width: 100% !important;

                .filter-sidebar-search-header {
                    display: none !important;
                }
            }

            .filter-sidebar-collapse {
                justify-content: center !important;
            }

            .favorite-item.channel-type,
            .filter-sidebar-collapse > .information-text,
            .favorite-item-text,
            .favorite-item-edit,
            .filter-sidebar-control,
            .filter-sidebar-group-title,
            .filter-sidebar-group.group-filters {
                display: none !important;
            }
        }
    }

    .visitors-content {
        width: 100%;
        min-width: calc(100% - 280px);
        padding: 30px;
        overflow-y: auto;
        height: calc(100vh - 117px);
        overflow-x: hidden;

        .save-filter-bar {
            display: flex;
            align-items: center;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            gap: 5px;
            white-space: nowrap;
            width: 200px;

            .filter-title {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
        }

        .tool-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 20px;

            .left-side {
                flex: 1;
                display: flex;
                align-items: center;
                gap: 10px;

                .search-section-wrapper {
                    position: relative;
                    flex: 1;
                    max-width: 600px;
                    margin-bottom: 0;

                    .search-mode-suggestion-popover {
                position: absolute;
                top: calc(100% + 6px);
                left: 0;
                z-index: 1050;
                background: #fff;
                border: 1px solid #e5e7eb;
                border-radius: 10px;
                box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
                padding: 16px;
                min-width: 320px;
                max-width: 400px;
                animation: suggestionSlideIn 0.15s ease-out;

                .suggestion-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 12px;

                    .suggestion-icon {
                        font-size: 24px;
                        color: #667eea;
                    }

                    .suggestion-title {
                        font-size: 16px;
                        font-weight: 600;
                        color: #22272d;
                    }
                }

                .suggestion-message {
                    font-size: 14px;
                    color: #6b7785;
                    line-height: 1.6;
                    margin-bottom: 16px;
                }

                .suggestion-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 8px;
                    padding-top: 12px;
                    border-top: 1px solid #e5e7eb;
                }
                    }
                }
            }

            .right-side {
                display: flex;
                align-items: center;
                gap: 4px;
                flex-shrink: 0;
            }
        }

        @keyframes suggestionSlideIn {
            from {
                opacity: 0;
                transform: translateY(-6px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .search-section {
            display: flex;
            align-items: center;
            gap: 8px;
            height: 40px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 0 12px 0px 3px;
            background: #fff;
            flex-shrink: 0;
            width: 100%;
        }

        .search-container {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
        }

        .search-icon-wrapper {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;

            .search-icon {
                font-size: 18px;
                color: #6b7785;
            }
        }

        .search-field-selector {
            flex-shrink: 0;
            margin-right: 8px;

            .select-button {
                white-space: nowrap;
                width: 140px;
                height: 40px;
                border: none;
                border-radius: 0;
                background: transparent;
            }
        }

        .search-input-wrapper {
            display: flex;
            align-items: center;
            flex: 1;
        }

        .search-input {
            flex: 1;
            height: 36px;
            border: none;
            outline: none;
            background: transparent;
            font-size: 14px;
            color: #22272d;

            &::placeholder {
                color: #bfbfbf;
            }
        }

        .clear-search-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            background: transparent;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            transition: background 0.2s ease;

            &:hover {
                background: #f0f0f0;
            }

            .material-icons {
                font-size: 16px;
                color: #6b7785;
            }
        }
    }
}

.filter-name {
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: 18px;

    &.active {
        span.material-icons {
            color: goldenrod;
        }
    }

    span.material-icons {
        margin-left: 5px;
    }
}

.custom-alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    border-radius: 4px;
    background-color: #e6f7ff;
    margin-bottom: 20px;

    .custom-alert-icon {
        font-size: 24px;
        color: #1890ff;
    }

    .custom-alert-content {
        ul {
            margin: 0;
            padding-left: 20px;
        }
    }
}

.export-progress-overlay {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 2000;
    pointer-events: none;

    .export-progress-card {
        pointer-events: all;
        background: #ffffff;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 12px 16px;
        min-width: 280px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);

        .title {
            font-weight: 600;
            margin-bottom: 4px;
        }

        .subtitle {
            color: #888;
            font-size: 12px;
            margin-bottom: 8px;
        }

        .detail {
            text-align: right;
            font-size: 12px;
            color: #555;
            margin-top: 6px;
        }
    }
}
</style>

<!-- Global Popover Styling - Scoped değil çünkü ant-popover body'de render oluyor -->
<style lang="scss">
.channel-contacts-modal {
    .ant-modal {
        max-width: 95vw;
        width: 95vw !important;
    }

    .ant-modal-body {
        padding: 20px;
        max-height: calc(100vh - 200px);
        overflow-y: auto;
    }
}

.channel-contacts-popover {
    .ant-popover-inner {
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05);
        border: 1px solid #e8e8e8;
    }

    .ant-popover-inner-content {
        padding: 8px 0;
    }

    .channel-contacts-menu {
        display: flex;
        flex-direction: column;
        min-width: 200px;

        .channel-contacts-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            cursor: pointer;
            transition: background-color 0.2s;
            font-size: 14px;
            color: #333;

            &:hover {
                background-color: #f5f5f5;
            }

            .platform-icon-wrapper {
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;

                .platform-icon {
                    width: 20px;
                    height: 20px;

                    .icon {
                        width: 20px;
                        height: 16px;
                    }
                }
            }
        }
    }
}

.referral-details-popover {
    .ant-popover-inner {
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05);
        border: 1px solid #e8e8e8;
    }

    .ant-popover-title {
        background: #f0f2f5;
        border-bottom: 1px solid #e8e8e8;
        font-weight: 600;
        font-size: 14px;
        padding: 8px 16px;
        margin: 0;
        border-radius: 8px 8px 0 0;
    }

    .ant-popover-inner-content {
        padding: 12px 16px;

        p {
            margin-bottom: 6px;
            font-size: 13px;
            line-height: 1.4;

            strong {
                color: #595959;
                font-weight: 500;
                min-width: 80px;
                display: inline-block;
            }
        }

        h4 {
            color: #1890ff;
            font-size: 13px;
            margin: 0 0 8px 0;
            font-weight: 600;
        }

        a {
            color: #1890ff;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}
</style>
